/* İçerik sözleşmeleri.
   data/*.json dosyalarının şekliyle birebir uyumludur — o dosyalar
   veritabanının tohumu olarak doğrudan bu şemalardan geçirilir. */

import { z } from 'zod';
import { WATERMARK_STYLES } from './media.ts';

/** Kaynak dil TR; EN isteğe bağlıdır ve yoksa TR'ye düşer. */
export const localized = z.object({
  tr: z.string().min(1),
  en: z.string().min(1).optional()
});
export type Localized = z.infer<typeof localized>;

/** Sözlükte karşılığı yoksa Türkçesine düşer — main.js'teki msg() ile aynı kural. */
export function pick(value: Localized, lang: 'tr' | 'en'): string {
  return lang === 'en' ? (value.en ?? value.tr) : value.tr;
}

/** Sayfa başlığındaki elle yazılmış SEO metinleri (plan §10 otomatikleşene dek). */
export const pageSeo = z.object({
  docTitle: localized,
  description: localized,
  ogTitle: localized,
  ogDescription: localized,
  /** JSON-LD `keywords` alanı; virgülle ayrılmış. */
  keywords: localized.optional(),
  /** Kök dizine göreli yol; mutlak adres yayın anında kurulur. */
  ogImage: z.string().min(1).nullable().default(null)
});
export type PageSeo = z.infer<typeof pageSeo>;

export const slug = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug yalnızca küçük harf, rakam ve tire içerebilir');

export const photo = z.object({
  id: z.string().min(1),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** en / boy — works.html ızgarasında `--r` değişkenine yazılır */
  ratio: z.number().positive(),
  alt: localized,
  lqip: z.string().startsWith('data:image/').optional(),
  capturedAt: z.string().datetime().nullable().optional(),
  status: z.enum(['processing', 'ready', 'failed']).default('ready')
});
export type Photo = z.infer<typeof photo>;

export const album = z.object({
  slug,
  title: localized,
  /** Başlığın `<em>` vurgulu hâli; düz `title` bundan türetilir. */
  titleHtml: localized,
  lede: localized,
  category: localized,
  year: z.number().int().min(1900).max(2200),
  coverPhotoId: z.string().min(1),
  watermarkStyle: z.enum(WATERMARK_STYLES).default('corner'),
  /** Bu albüm arama motorlarından gizlensin mi (yayınlanmamış kampanya). */
  noindex: z.boolean().default(false),
  published: z.boolean().default(false),
  seo: pageSeo.nullable().default(null),
  photoIds: z.array(z.string().min(1)).min(1)
});
export type Album = z.infer<typeof album>;

/** works.html ızgarasındaki bir kare. */
export const work = z.object({
  id: z.string().min(1),
  photoId: z.string().min(1),
  /** Doluysa kare bir albüm kapağıdır; tıklayınca albüm sayfasına gider. */
  albumSlug: slug.nullable().default(null),
  category: localized,
  year: z.number().int().min(1900).max(2200),
  /** CSS `--r` (en/boy) ve `--s` (ızgarada kapladığı görece alan) */
  ratio: z.number().positive(),
  weight: z.number().positive().default(1),
  position: z.number().int().nonnegative(),
  /** Ana sayfa karuselindeki sıra; karuselde yer almayan kareler için null. */
  featuredIndex: z.number().int().nonnegative().nullable().default(null),
  published: z.boolean().default(true)
});
export type Work = z.infer<typeof work>;

/**
 * Yazının kapak görseli. Stüdyo kendi karesini seçene kadar buradaki
 * adresler dışarıdan gelen yer tutuculardır — bu yüzden `photos`
 * tablosuna girmezler: imzalı adres, filigran ve adli iz hattı yalnızca
 * stüdyonun kendi karelerini taşır (plan §5). `coverPhotoId` dolduğu gün
 * bu blok gereksizleşir.
 */
export const postCover = z.object({
  url: z.string().url(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
  alt: localized,
  caption: localized,
  /** Günlük listesindeki kart aynı görseli daha küçük ister. */
  listUrl: z.string().url(),
  listW: z.number().int().positive(),
  listH: z.number().int().positive(),
  listAlt: localized
});
export type PostCover = z.infer<typeof postCover>;

export const post = z.object({
  slug,
  title: localized,
  /** Kırıntı yolunda ve yazılar arası gezinmede kullanılan kısa ad. */
  shortTitle: localized,
  /** Başlığın `<em>` vurgulu hâli; düz `title` bundan türetilir. */
  titleHtml: localized,
  lede: localized,
  excerpt: localized.optional(),
  category: localized,
  readMinutes: z.number().int().positive(),
  coverPhotoId: z.string().min(1).nullable().default(null),
  cover: postCover.nullable().default(null),
  publishedAt: z.string().date(),
  seo: pageSeo.nullable().default(null),
  /** Gövde HTML'i; TR zorunlu, EN isteğe bağlı. */
  bodyHtml: localized.optional()
});
export type Post = z.infer<typeof post>;

export const SERVICE_KEYS = [
  'editorial',
  'campaign',
  'lookbook',
  'video',
  'digital',
  'other'
] as const;
export type ServiceKey = (typeof SERVICE_KEYS)[number];

export const service = z.object({
  key: z.enum(SERVICE_KEYS),
  title: localized,
  desc: localized,
  meta: localized,
  bookable: z.boolean().default(true),
  position: z.number().int().nonnegative()
});
export type Service = z.infer<typeof service>;
